import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  createBiometricDevice,
  fetchBiometricDashboard,
  fetchBiometricDevices,
  fetchBiometricProviders,
  fetchBiometricSyncLogs,
  fetchDirectAdmsServerConfig,
  healthCheckBiometricDevice,
  syncBiometricDevice,
  updateBiometricDevice,
} from "../../../Moduels/attendance/wfApi";

const CONNECTION_PIPELINES = {
  cloud_connector: {
    label: "Cloud / Connector",
    description: "Mission → Connector → Brixigo Cloud → Brixigo",
    defaultMode: "cloud",
  },
  direct_adms: {
    label: "Direct ADMS",
    description: "ADMS Device → Brixigo directly (HTTPS /iclock)",
    defaultMode: "direct_adms",
  },
};

const emptyForm = {
  terminal_code: "",
  device_name: "",
  vendor: "essl",
  model: "",
  serial_number: "",
  ip_address: "",
  port: "",
  communication_mode: "cloud",
  timezone: "Asia/Kolkata",
  location: "",
  device_type: "biometric",
  connection_pipeline: "cloud_connector",
};

export default function WfDevices() {
  const [tab, setTab] = useState("devices");
  const [devices, setDevices] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [providers, setProviders] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [secrets, setSecrets] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [syncLogs, setSyncLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [admsConfig, setAdmsConfig] = useState(null);

  const load = async () => {
    setErr("");
    try {
      const [devs, dash, prov, adms] = await Promise.all([
        fetchBiometricDevices(),
        fetchBiometricDashboard(),
        fetchBiometricProviders(),
        fetchDirectAdmsServerConfig().catch(() => null),
      ]);
      setDevices(devs);
      setDashboard(dash);
      setProviders(prov);
      setAdmsConfig(adms);
    } catch (e) {
      setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e?.response?.data?.detail?.message || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onRegister = async (e) => {
    e.preventDefault();
    if (!form.terminal_code.trim()) return;
    setBusy(true);
    setMsg("");
    setErr("");
    setSecrets(null);
    try {
      const payload = {
        ...form,
        communication_mode:
          form.connection_pipeline === "direct_adms" ? "direct_adms" : form.communication_mode,
        port: form.port ? Number(form.port) : null,
      };
      delete payload.connection_pipeline;
      if (form.connection_pipeline === "direct_adms" && !form.serial_number.trim()) {
        setErr("Serial number is required for Direct ADMS devices.");
        setBusy(false);
        return;
      }
      const created = await createBiometricDevice(payload);
      setMsg(`Device registered: ${created.device_name || created.terminal_code}`);
      if (created.api_token || created.webhook_secret) {
        setSecrets({
          api_token: created.api_token,
          webhook_secret: created.webhook_secret,
        });
      }
      setForm(emptyForm);
      await load();
    } catch (ex) {
      setErr(typeof ex?.response?.data?.detail === "string" ? ex.response.data.detail : ex?.response?.data?.detail?.message || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const onSync = async (deviceId) => {
    setBusy(true);
    setErr("");
    try {
      const result = await syncBiometricDevice(deviceId, { mode: "auto" });
      setMsg(`Sync ${result.status}: accepted ${result.accepted ?? 0} / ${result.total ?? 0}`);
      await load();
    } catch (ex) {
      setErr(typeof ex?.response?.data?.detail === "string" ? ex.response.data.detail : ex?.response?.data?.detail?.message || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const onHealth = async (deviceId) => {
    setBusy(true);
    try {
      const h = await healthCheckBiometricDevice(deviceId);
      setMsg(`Health: ${h.online ? "online" : "offline"} — ${h.message || ""}`);
      await load();
    } catch (ex) {
      setErr(typeof ex?.response?.data?.detail === "string" ? ex.response.data.detail : ex?.response?.data?.detail?.message || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const onDeactivate = async (deviceId) => {
    if (!window.confirm("Deactivate this device?")) return;
    setBusy(true);
    try {
      await updateBiometricDevice(deviceId, { status: "inactive" });
      setMsg("Device deactivated.");
      await load();
    } catch (ex) {
      setErr(typeof ex?.response?.data?.detail === "string" ? ex.response.data.detail : ex?.response?.data?.detail?.message || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const loadLogs = async (deviceId) => {
    setSelectedId(deviceId);
    try {
      setSyncLogs(await fetchBiometricSyncLogs(deviceId));
      setTab("sync");
    } catch (ex) {
      setErr(typeof ex?.response?.data?.detail === "string" ? ex.response.data.detail : ex?.response?.data?.detail?.message || ex.message);
    }
  };

  const healthTone = (h) => {
    if (h === "online" || h === "healthy") return "text-emerald-700";
    if (h === "degraded") return "text-amber-700";
    if (h === "offline") return "text-red-700";
    return "text-gray-600";
  };

  return (
    <div className="p-6 w-full max-w-full min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Biometric Devices</h1>
          <p className="text-sm text-gray-500">
            Register biometric devices via Cloud/Connector (Pipeline 1) or Direct ADMS (Pipeline 2).
            Attendance appears on the same pages regardless of pipeline.
          </p>
        </div>
        <Link
          to="/attendance/employee-device-mapping"
          className="text-sm text-teal-700 underline font-medium"
        >
          Employee ↔ Device mapping
        </Link>
      </div>

      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            ["Total", dashboard.total_devices],
            ["Online", dashboard.online],
            ["Offline", dashboard.offline],
            ["Degraded", dashboard.degraded],
            ["Pending logs", dashboard.pending_logs],
          ].map(([label, value]) => (
            <div key={label} className="bg-white border rounded-xl p-3">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="text-xl font-semibold">{value ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          ["devices", "Devices"],
          ["register", "Register"],
          ["sync", "Sync history"],
          ["providers", "Providers"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`px-3 py-1.5 rounded-lg text-sm border ${
              tab === id ? "bg-teal-600 text-white border-teal-600" : "bg-white text-gray-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {msg && <div className="mb-2 text-green-700 text-sm">{msg}</div>}
      {err && <div className="mb-2 text-red-600 text-sm">{err}</div>}
      {secrets && (
        <div className="mb-4 p-3 border border-amber-300 bg-amber-50 rounded-xl text-sm">
          <div className="font-medium mb-1">Store these secrets now — shown once</div>
          <div className="font-mono break-all">API token: {secrets.api_token}</div>
          <div className="font-mono break-all mt-1">Webhook secret: {secrets.webhook_secret}</div>
        </div>
      )}

      {tab === "register" && (
        <form onSubmit={onRegister} className="bg-white border rounded-xl p-4 grid md:grid-cols-2 gap-3 mb-6">
          <div className="md:col-span-2 grid gap-2">
            <span className="text-sm text-gray-600 font-medium">Connection method</span>
            <div className="grid md:grid-cols-2 gap-3">
              {Object.entries(CONNECTION_PIPELINES).map(([key, cfg]) => (
                <label
                  key={key}
                  className={`border rounded-xl p-3 cursor-pointer ${
                    form.connection_pipeline === key ? "border-teal-600 bg-teal-50" : "border-gray-200"
                  }`}
                >
                  <input
                    type="radio"
                    name="connection_pipeline"
                    className="mr-2"
                    checked={form.connection_pipeline === key}
                    onChange={() =>
                      setForm({
                        ...form,
                        connection_pipeline: key,
                        communication_mode: cfg.defaultMode,
                      })
                    }
                  />
                  <span className="font-medium text-sm">{cfg.label}</span>
                  <div className="text-xs text-gray-500 mt-1 ml-5">{cfg.description}</div>
                </label>
              ))}
            </div>
          </div>

          {form.connection_pipeline === "direct_adms" && admsConfig && (
            <div className="md:col-span-2 p-3 border border-blue-200 bg-blue-50 rounded-xl text-sm">
              <div className="font-medium mb-2">Configure on the physical device</div>
              <div className="grid md:grid-cols-2 gap-1 font-mono text-xs">
                <div>Server Mode: ADMS</div>
                <div>Enable Domain Name: ON</div>
                <div>Server Address: {admsConfig.server_address}</div>
                <div>Server Port: {admsConfig.server_port}</div>
                <div>Enable Proxy Server: OFF</div>
                <div>HTTPS: ON</div>
              </div>
              {!admsConfig.enabled && (
                <div className="text-amber-700 mt-2 text-xs">
                  Direct ADMS is disabled on the server (ADMS_DIRECT_ENABLED=false). Register the device now;
                  enable the flag before go-live.
                </div>
              )}
            </div>
          )}

          {[
            ["terminal_code", "Terminal code *"],
            ["device_name", "Device name"],
            ["model", "Model (e.g. MB01K / Face terminal)"],
            [
              "serial_number",
              form.connection_pipeline === "direct_adms" ? "Serial number (ADMS SN) *" : "Serial number",
            ],
            ["ip_address", "IP address (LAN/VPN pull)"],
            ["port", "Port"],
            ["location", "Location label"],
            ["timezone", "Timezone"],
          ].map(([key, label]) => (
            <label key={key} className="text-sm grid gap-1">
              <span className="text-gray-600">{label}</span>
              <input
                className="border rounded px-3 py-2"
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                disabled={form.connection_pipeline === "direct_adms" && key === "ip_address"}
              />
            </label>
          ))}
          <label className="text-sm grid gap-1">
            <span className="text-gray-600">Vendor</span>
            <select
              className="border rounded px-3 py-2"
              value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })}
            >
              <option value="essl">eSSL</option>
              <option value="matrix">Matrix COSEC / BioFace</option>
              <option value="zkteco">ZKTeco</option>
              <option value="realtime">Realtime</option>
            </select>
          </label>
          {form.connection_pipeline === "cloud_connector" && (
            <label className="text-sm grid gap-1">
              <span className="text-gray-600">Communication mode</span>
              <select
                className="border rounded px-3 py-2"
                value={form.communication_mode}
                onChange={(e) => setForm({ ...form, communication_mode: e.target.value })}
              >
                {["push", "pull", "cloud", "lan", "vpn", "hybrid"].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={busy}
              className="bg-teal-600 text-white py-2 px-4 rounded-lg disabled:opacity-60"
            >
              Register device
            </button>
          </div>
        </form>
      )}

      {tab === "devices" && (
        <div className="overflow-x-auto border rounded-xl bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3">Device</th>
                <th className="text-left p-3">Vendor</th>
                <th className="text-left p-3">Mode</th>
                <th className="text-left p-3">Health</th>
                <th className="text-left p-3">Last activity</th>
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.device_id} className="border-t">
                  <td className="p-3">
                    <div className="font-medium">{d.device_name || d.terminal_code}</div>
                    <div className="text-xs text-gray-500">{d.terminal_code}</div>
                  </td>
                  <td className="p-3">
                    {d.vendor || "—"}
                    {d.model ? ` / ${d.model}` : ""}
                  </td>
                  <td className="p-3">
                    {d.communication_mode === "direct_adms" ? "Direct ADMS" : d.communication_mode}
                  </td>
                  <td className={`p-3 ${healthTone(d.health_status)}`}>{d.health_status || "unknown"}</td>
                  <td className="p-3">
                    {d.communication_mode === "direct_adms"
                      ? d.last_heartbeat_at
                        ? `ADMS seen ${new Date(d.last_heartbeat_at).toLocaleString()}`
                        : "—"
                      : d.last_sync_at
                        ? new Date(d.last_sync_at).toLocaleString()
                        : "—"}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      {d.communication_mode !== "direct_adms" && (
                        <button
                          type="button"
                          className="text-teal-700 underline"
                          disabled={busy}
                          onClick={() => onSync(d.device_id)}
                        >
                          Sync
                        </button>
                      )}
                      <button
                        type="button"
                        className="text-teal-700 underline"
                        disabled={busy}
                        onClick={() => onHealth(d.device_id)}
                      >
                        Health
                      </button>
                      <button
                        type="button"
                        className="text-gray-700 underline"
                        onClick={() => loadLogs(d.device_id)}
                      >
                        Logs
                      </button>
                      {d.is_active !== false && (
                        <button
                          type="button"
                          className="text-red-600 underline"
                          disabled={busy}
                          onClick={() => onDeactivate(d.device_id)}
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!devices.length && (
                <tr>
                  <td colSpan={6} className="p-4 text-gray-500 text-center">
                    No devices registered.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "sync" && (
        <div className="bg-white border rounded-xl p-4">
          <div className="text-sm text-gray-600 mb-2">
            Sync history {selectedId ? `for device ${selectedId}` : "(select Logs on a device)"}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-2">When</th>
                <th className="text-left p-2">Received</th>
                <th className="text-left p-2">Accepted</th>
                <th className="text-left p-2">Latency</th>
                <th className="text-left p-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {syncLogs.map((l) => (
                <tr key={l.sync_id} className="border-t">
                  <td className="p-2">{l.synced_at ? new Date(l.synced_at).toLocaleString() : "—"}</td>
                  <td className="p-2">{l.events_received}</td>
                  <td className="p-2">{l.events_accepted}</td>
                  <td className="p-2">{l.latency_ms ?? "—"}</td>
                  <td className="p-2 text-red-600">{l.error_message || "—"}</td>
                </tr>
              ))}
              {!syncLogs.length && (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-gray-500">
                    No sync logs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "providers" && (
        <div className="grid md:grid-cols-2 gap-3">
          {providers.map((p) => (
            <div key={p.vendor_code} className="bg-white border rounded-xl p-4">
              <div className="font-medium">{p.display_name}</div>
              <div className="text-xs text-gray-500 mt-1">Code: {p.vendor_code}</div>
              <div className="text-sm mt-2">
                Push: {p.supports_push ? "Yes" : "No"} · Pull: {p.supports_pull ? "Yes" : "No"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
